import { useCallback } from 'react'

import type { Logger } from '@vp/ubik-logging'

import { upsertCart, upsertCartMultiWork } from '../clients/CartManagerClient'
import { getCart } from '../clients/VistaCartClient'
import type { WorkId } from '../types/WorkId'
import { useAuthorization } from '../components/root/contexts/AuthorizationProvider'
import { useTenant } from '../components/root/contexts/TenantContext'

export type IUseCart = {
  upsertCartAsync: (workId: WorkId, culture: string, authorizationHeader: string | undefined, logger: Logger) => Promise<void>
  upsertCartMultiWorkAsync: (workIds: string[], culture: string, authorizationHeader: string | undefined, logger: Logger) => Promise<void>
  verifyCartContainsWorkIds: (workIds: string[], culture: string, authorizationHeader: string | undefined, logger: Logger) => Promise<boolean>
}

export const useCart = () => {
  const { shopperId } = useAuthorization()
  const { cartTenant } = useTenant()

  const upsertCartAsync = useCallback(
    async (workId: WorkId, culture: string, authorizationHeader: string | undefined, logger: Logger): Promise<void> => {
      if (!authorizationHeader) {
        const error = new Error('Cannot add to cart: user not authenticated')
        logger.error('Cart upsert failed - missing authorization', error, { workId, culture })
        throw error
      }
      await upsertCart(cartTenant, workId, culture, authorizationHeader, logger)
    },
    [cartTenant],
  )

  const upsertCartMultiWorkAsync = useCallback(
    async (workIds: WorkId[], culture: string, authorizationHeader: string | undefined, logger: Logger): Promise<void> => {
      if (!authorizationHeader) {
        const error = new Error('Cannot add to cart: user not authenticated')
        logger.error('Cart multi-work upsert failed - missing authorization', error, { workIds, culture })
        throw error
      }
      await upsertCartMultiWork(cartTenant, workIds, culture, authorizationHeader, logger)
    },
    [cartTenant],
  )

  const verifyCartContainsWorkIds = useCallback(
    async (workIds: string[], culture: string, authorizationHeader: string | undefined, logger: Logger): Promise<boolean> => {
      if (!authorizationHeader) {
        const error = new Error('Cannot verify work is in cart: user not authenticated')
        logger.error('Cart verification failed - missing authorization', error, {
          workIds,
          culture,
        })
        return false
      }

      if (!shopperId) {
        const error = new Error('Cannot verify work is in cart: missing shopperId')
        logger.error('Cart verification failed - missing shopperId', error, { workIds, culture })
        return false
      }

      try {
        const cartResponse = await getCart(culture, shopperId, authorizationHeader, logger)

        const cartWorkIds = new Set<string>()
        for (const lineItem of cartResponse.lineItems) {
          if (lineItem.customOrderData.analytics.workId) {
            cartWorkIds.add(lineItem.customOrderData.analytics.workId)
          }
        }

        const foundWorkIds = workIds.filter((workId) => cartWorkIds.has(workId))
        const allFound = foundWorkIds.length === workIds.length

        return allFound
      } catch (error) {
        logger.error('Cart verification failed - error fetching cart', error as Error, { workIds, culture })
        return false
      }
    },
    [shopperId],
  )

  return {
    upsertCartAsync,
    upsertCartMultiWorkAsync,
    verifyCartContainsWorkIds,
  }
}
